import { Button, ModalActions, ModalContent, ModalTitle } from "@kaltura/ds-react-components";
import { useState } from "react";
import CreateTownhallForm, { CreateTownhallFormProps } from "./form/CreateTownhallForm";
import CreateTownhallFormContainer from "./form/CreateTownhallFormContainer";
import { translate } from "@kaltura/ds-react-utils";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";

export interface CreateTownhallModalProps extends CreateTownhallFormProps {
    /**
     * is the modal currently open
     */
    open: boolean;

    /**
     * cancel button callback
     */
    onCancel: () => void;

    /**
     * mode of the townhall
     */
    mode?: "kwebcast" | "live-studio";

    /**
     * URL to fetch templates from (required for live-studio mode)
     */
    templatesUrl?: string;
}

const formId = "create-townhall-form";

/**
 * Create Townhall Modal Content component
 */
export const CreateTownhallModalContent = (props: CreateTownhallModalProps) => {
    const sendButtonAnalytics = useButtonAnalytics();
    const { open, onCancel, mode = "kwebcast", templatesUrl, ...formProps } = props;

    const [processing, setProcessing] = useState(false);
    const [disableButton, setDisableButton] = useState(true);

    const baseClassName = "kms-ds-create-townhall-modal";

    const updateProcessing = (value: boolean) => {
        setProcessing(value);
    };

    const updateDisableButton = (value: boolean) => {
        setDisableButton(value);
    };

    const handleCancel = () => {
        // send analytics
        if (mode === "kwebcast") {
            sendButtonAnalytics("Townhall - cancel", ButtonClickAnalyticsType.CLOSE);
        }
        else {
            sendButtonAnalytics("Live-studio - cancel", ButtonClickAnalyticsType.CLOSE);
        }
        onCancel();
    };

    const isButtonDisabled = processing || disableButton;

    // get title by current mode
    const title = mode === "kwebcast" ? translate("Create town hall") : translate("Create a Live Studio");

    return (
        <>
            <ModalTitle className={`${baseClassName}-title`}>{title}</ModalTitle>
            <ModalContent className={`${baseClassName}-content`}>
                {open && mode === "kwebcast" && (
                    <CreateTownhallForm
                        {...formProps}
                        formId={formId}
                        updateProcessing={updateProcessing}
                        updateDisableButton={updateDisableButton}
                    />
                )}
                {open && mode === "live-studio" && templatesUrl && (
                    <CreateTownhallFormContainer
                        {...formProps}
                        templatesUrl={templatesUrl}
                        formId={formId}
                        updateProcessing={updateProcessing}
                        updateDisableButton={updateDisableButton}
                    />
                )}
            </ModalContent>
            <ModalActions className={`${baseClassName}-actions`}>
                <Button
                    onClick={handleCancel}
                    variant={"borderless"}
                    color={"secondary"}
                    className={`${baseClassName}-actions-cancel`}
                >
                    {translate("Cancel")}
                </Button>
                <Button
                    type={"submit"}
                    loading={processing}
                    disabled={isButtonDisabled}
                    className={`${baseClassName}-actions-submit`}
                    form={formId}
                >
                    {translate("Create")}
                </Button>
            </ModalActions>
        </>
    );
};
